import { useMemo } from 'react';

import {
  GetAppliedResourceLineageParentsQuery,
  GetDefinitionResourceLineageParentsQuery,
  ResourceNodeType,
} from '@/__generated__/graphql';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import {
  GetAppliedResourceLineageParents,
  GetDefinitionResourceLineageParents,
} from '../discovery/lineage';
import { useEnvironmentScope } from '../useEnvironmentScope';
import { useQueryStable } from '../useQueryStable';

export type ModelWithChildrenAndParentsResult = ReturnType<
  typeof useResourceParents
>['data'];
export const useResourceParents = (
  uniqueIds: string[],
  type?: 'applied' | 'definition',
) => {
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();

  const queryType =
    type === 'applied'
      ? GetAppliedResourceLineageParents
      : GetDefinitionResourceLineageParents;

  const [{ fetching, error, data }] = useQueryStable<
    GetAppliedResourceLineageParentsQuery | GetDefinitionResourceLineageParentsQuery
  >({
    query: queryType,
    variables: {
      environmentId,
      filter: {
        uniqueIds,
        types: [
          'Model',
          'Macro',
          'Snapshot',
          'Seed',
          'Source',
          'Exposure',
          'Metric',
          'Test',
          'SemanticModel',
          'SavedQuery',
        ] as ResourceNodeType[],
        tags: null,
      },
      size: uniqueIds.length,
    },
    pause: !environmentId || !uniqueIds.length || userId === 'loading' || !type,
  });

  const typedData =
    type === 'applied'
      ? (data as GetAppliedResourceLineageParentsQuery)?.environment?.applied
      : (data as GetDefinitionResourceLineageParentsQuery)?.environment?.definition;

  const modelQuery = useMemo(() => {
    const resources = typedData?.resources;
    return {
      fetching,
      error,
      data: resources,
    };
  }, [fetching, error, typedData]);
  return modelQuery;
};
